import React, { useState, useEffect } from 'react';
import './AdminDashboard.css';

function AdminDashboard() {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('generate-code'); // Default section
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [newCodeConfig, setNewCodeConfig] = useState({
    expires: '',
    maxUses: 10, // Default to 10
    description: '',
    prefix: 'PAPITA',
    count: 1,
    allowWithoutRegistration: false, // Default: requires registration
    neverExpire: false // Never expire checkbox (exclusive to employers)
  });
  const [emailModal, setEmailModal] = useState(null); // { code: 'CODE', email: '' }
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [editModal, setEditModal] = useState(null); // { code: 'CODE', codeData: {...}, updates: {...} }
  const [updatingCode, setUpdatingCode] = useState(false);
  const [savingCodes, setSavingCodes] = useState(false);
  const [databaseCodes, setDatabaseCodes] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState(new Set()); // For bulk delete
  const [searchQuery, setSearchQuery] = useState(''); // For searching codes
  const [lastGeneratedCode, setLastGeneratedCode] = useState(null); // For displaying newly generated code
  const [users, setUsers] = useState([]); // For user management
  const [usersLoading, setUsersLoading] = useState(false);

  // Check if admin password is set and verify
  // Use sessionStorage instead of localStorage - automatically clears when window/tab closes
  useEffect(() => {
    const adminConfig = sessionStorage.getItem('admin_config');
    if (adminConfig) {
      try {
        const config = JSON.parse(adminConfig);
        if (config.authenticated && config.expires > Date.now()) {
          setIsAuthenticated(true);
        } else {
          // Session expired
          sessionStorage.removeItem('admin_config');
        }
      } catch (e) {
        // Invalid config
        sessionStorage.removeItem('admin_config');
      }
    }
  }, []);

  // Load existing codes from database when authenticated
  useEffect(() => {
    if (isAuthenticated && (activeSection === 'invite-codes' || activeSection === 'generate-code')) {
      loadDatabaseCodes();
    }
  }, [isAuthenticated, activeSection]);

  const loadDatabaseCodes = async () => {
    try {
      const response = await fetch('/api/invite-codes');
      if (response.ok) {
        const data = await response.json();
        if (data.codes) {
          // Convert database format to display format
          const formattedCodes = data.codes.map(code => ({
            code: code.code,
            data: {
              used: code.used,
              usedCount: code.usedCount || 0, // Include used_count from database
              expires: code.expires,
              maxUses: code.maxUses,
              created: code.createdAt ? code.createdAt.split('T')[0] : new Date().toISOString().split('T')[0],
              description: code.description || 'No description',
              active: code.active !== false // Include active flag (default to true if not present for backward compatibility)
            },
            fromDatabase: true
          }));
          setDatabaseCodes(formattedCodes);
        }
      }
    } catch (error) {
      console.error('Error loading codes from database:', error);
    }
  };

  // Load users when authenticated and on users section or invite-codes section
  useEffect(() => {
    if (isAuthenticated && (activeSection === 'users' || activeSection === 'invite-codes')) {
      loadUsers();
    }
  }, [isAuthenticated, activeSection]);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await fetch('/api/users');
      if (response.ok) {
        const data = await response.json();
        if (data.users) {
          setUsers(data.users);
        }
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setError(null);

    if (!password) {
      setError('');
      return;
    }

    // Get admin password from localStorage
    // The password is set when you generate the admin URL with --password flag
    // For now, we'll check against a simple hash stored in localStorage
    // In production, this should be more secure (e.g., check against server)
    
    const storedPasswordHash = localStorage.getItem('admin_password_hash');
    
    // First time setup - accept password and store hash
    if (!storedPasswordHash) {
      const hash = btoa(password).replace(/[+/=]/g, '').substring(0, 20);
      localStorage.setItem('admin_password_hash', hash);
      setIsAuthenticated(true);
      // Use sessionStorage - clears when window/tab closes
      sessionStorage.setItem('admin_config', JSON.stringify({
        authenticated: true,
        expires: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
      }));
      setPassword('');
      return;
    }
    
    // Verify password
    const enteredHash = btoa(password).replace(/[+/=]/g, '').substring(0, 20);
    if (storedPasswordHash === enteredHash) {
      setIsAuthenticated(true);
      // Use sessionStorage - clears when window/tab closes
      sessionStorage.setItem('admin_config', JSON.stringify({
        authenticated: true,
        expires: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
      }));
      setPassword('');
    } else {
      setError('Invalid');
      // Clear password field on error
      setTimeout(() => {
        setPassword('');
        setError(null);
      }, 2000);
    }
  };

  const generateInviteCode = async (e) => {
    if (e) {
      e.preventDefault();
    }
    // Validation is already in generateInviteCodeWithConfig, but we'll call it
    await generateInviteCodeWithConfig();
  };

  const generateInviteCodeWithConfig = async (config = null) => {
    const codeConfig = config || newCodeConfig;
    try {
      // Validate required fields and show prompt
      if (!codeConfig.prefix || codeConfig.prefix.trim() === '') {
        setError('Code Prefix is required');
        alert('⚠️ Code Prefix is required. Please enter a prefix for the access code.');
        return;
      }
      
      // Only require expires date if neverExpire is false
      if (!codeConfig.neverExpire && (!codeConfig.expires || codeConfig.expires.trim() === '')) {
        setError('Expires date is required');
        alert('⚠️ Expires date is required. Please select an expiration date, or check "Never Expire".');
        return;
      }
      
      if (!codeConfig.count || parseInt(codeConfig.count) < 1) {
        setError('Count is required and must be at least 1');
        alert('⚠️ Count is required. Please enter the number of codes to generate (minimum 1).');
        return;
      }

      setSavingCodes(true);
      setError(null);

      const count = parseInt(codeConfig.count) || 1;
      const savedCodes = [];
      
      for (let i = 0; i < count; i++) {
        // If neverExpire is checked (employers only), use just the prefix
        // Otherwise, generate secure random code with prefix
        let code;
        if (codeConfig.neverExpire) {
          code = codeConfig.prefix; // Simple code: just the prefix
        } else {
          // Generate secure random code
          const array = new Uint8Array(10);
          crypto.getRandomValues(array);
          const randomCode = Array.from(array, byte => 
            byte.toString(36).toUpperCase()
          ).join('').substring(0, 10);
          
          code = `${codeConfig.prefix}${randomCode}`;
        }
        const created = new Date().toISOString().split('T')[0];
        // If neverExpire is checked, set expiresDate to null (never expires) and maxUses to null (unlimited)
        const expiresDate = codeConfig.neverExpire ? null : (codeConfig.expires ? new Date(codeConfig.expires + 'T23:59:59').toISOString() : null);
        const maxUsesValue = codeConfig.neverExpire ? null : (codeConfig.maxUses ? parseInt(codeConfig.maxUses) : null);
        const description = codeConfig.count > 1 
          ? `${codeConfig.description || 'Generated via admin dashboard'} #${i + 1}` 
          : (codeConfig.description || 'Generated via admin dashboard');
        
        // Save to database
        try {
          const allowWithoutReg = codeConfig.allowWithoutRegistration === true; // Explicitly check for true
          console.log('Saving code with allowWithoutRegistration:', allowWithoutReg, 'from config:', codeConfig.allowWithoutRegistration);
          
          // For employers only (neverExpire checked): set active = true (special case)
          // Otherwise: active = false (default, requires registration)
          const activeValue = codeConfig.neverExpire ? true : false;
          
          const response = await fetch('/api/invite-codes', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              code: code,
              expires: expiresDate,
              maxUses: maxUsesValue,
              description: description,
              createdBy: 'admin',
              allowWithoutRegistration: allowWithoutReg,
              active: activeValue // Special case: employers get active = true
            }),
          });

          if (response.ok) {
            const data = await response.json();
            savedCodes.push({
              code: data.code.code,
              data: {
                used: data.code.used || false,
                expires: codeConfig.neverExpire ? null : (codeConfig.expires || null),
                maxUses: maxUsesValue,
                created,
                description
              },
              fromDatabase: true
            });
          } else {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to save code to database');
          }
        } catch (saveError) {
          console.error('Error saving code to database:', saveError);
          // Still add to local list even if database save fails
          savedCodes.push({
            code,
            data: {
              used: false,
              expires: codeConfig.neverExpire ? null : (codeConfig.expires || null),
              maxUses: maxUsesValue,
              created,
              description
            },
            fromDatabase: false,
            saveError: saveError.message
          });
        }
      }

      // Separate codes by whether they're in the database or not
      const dbCodes = savedCodes.filter(c => c.fromDatabase);
      const localCodes = savedCodes.filter(c => !c.fromDatabase);
      
      if (dbCodes.length > 0) {
        setDatabaseCodes([...databaseCodes, ...dbCodes]);
      }
      if (localCodes.length > 0) {
        setGeneratedCodes([...generatedCodes, ...localCodes]);
      }
      
      // Clear any previous errors
      setError(null);
      
      // Store the last generated code for display
      if (savedCodes.length > 0) {
        setLastGeneratedCode(savedCodes[0]); // Show the first generated code
      }
      
      // Reset form (keep user's prefix and preferences, but clear date and description)
      setNewCodeConfig(prev => ({
        ...prev,
        expires: '',
        description: '',
        count: 1,
        neverExpire: false // Reset neverExpire checkbox
        // Keep: prefix, maxUses, allowWithoutRegistration
      }));

      // Don't show alert, code will be displayed on page
    } catch (err) {
      setError('Failed to generate code: ' + err.message);
      alert('❌ Error: ' + err.message);
    } finally {
      setSavingCodes(false);
    }
  };

  const copyCodeToClipboard = (code) => {
    const codeString = `  '${code.code}': {
    used: false,
    expires: ${code.data.expires ? `'${code.data.expires}'` : 'null'},
    maxUses: ${code.data.maxUses !== null ? code.data.maxUses : 'null'},
    created: '${code.data.created}',
    description: '${code.data.description}'
  },`;
    
    navigator.clipboard.writeText(codeString).then(() => {
      alert('Code snippet copied to clipboard!');
    });
  };

  const copyCodeString = (code) => {
    navigator.clipboard.writeText(code.code).then(() => {
      alert(`Code "${code.code}" copied to clipboard!`);
    });
  };

  const handleUseCodeForRegistration = (code) => {
    // Redirect to main page with invite code in URL
    window.location.href = `/?invite=${code.code}`;
  };

  const generateQuickCode = async () => {
    // Only check expiration date if neverExpire is not checked
    if (!newCodeConfig.neverExpire && (!newCodeConfig.expires || newCodeConfig.expires.trim() === '')) {
      setError('Expiration date is required');
      alert('⚠️ An expiration date has not been set. Please select an expiration date or check "Never Expire" before generating a quick code.');
      return;
    }
    
    // Calculate tomorrow's date as fallback (only used if neverExpire is false and expires not set)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    
    // Use current form values, but set defaults for missing fields
    // When neverExpire is checked, expires and maxUses are null, count is 1
    const quickConfig = {
      prefix: newCodeConfig.prefix || 'PAPITA',
      expires: newCodeConfig.neverExpire ? null : (newCodeConfig.expires || tomorrowStr),
      maxUses: newCodeConfig.neverExpire ? null : (newCodeConfig.maxUses || 10),
      description: newCodeConfig.description || 'Quick access code',
      count: newCodeConfig.neverExpire ? 1 : (newCodeConfig.count || 1), // Count is always 1 when neverExpire
      allowWithoutRegistration: newCodeConfig.allowWithoutRegistration === true, // Explicitly check for true
      neverExpire: newCodeConfig.neverExpire === true // Include neverExpire flag
    };
    
    console.log('Generate Quick Code - checkbox state:', newCodeConfig.allowWithoutRegistration);
    console.log('Generate Quick Code - config allowWithoutRegistration:', quickConfig.allowWithoutRegistration);
    
    // Generate with quick config
    await generateInviteCodeWithConfig(quickConfig);
  };

  const toggleCodeSelection = (code) => {
    const newSelected = new Set(selectedCodes);
    if (newSelected.has(code)) {
      newSelected.delete(code);
    } else {
      newSelected.add(code);
    }
    setSelectedCodes(newSelected);
  };

  const toggleSelectAll = () => {
    const allCodes = [...databaseCodes, ...generatedCodes].filter((code, index, self) => 
      index === self.findIndex(c => c.code === code.code)
    );
    
    if (selectedCodes.size === allCodes.length && allCodes.length > 0) {
      // Deselect all
      setSelectedCodes(new Set());
    } else {
      // Select all
      setSelectedCodes(new Set(allCodes.map(c => c.code)));
    }
  };

  const getAllCodes = () => {
    return [...databaseCodes, ...generatedCodes].filter((code, index, self) => 
      index === self.findIndex(c => c.code === code.code)
    );
  };

  const filterCodesBySearch = (codes) => {
    if (!searchQuery.trim()) return codes;
    const query = searchQuery.toLowerCase();
    return codes.filter(code => 
      code.code.toLowerCase().includes(query) ||
      (code.data.description && code.data.description.toLowerCase().includes(query)) ||
      (code.data.assignedTo && code.data.assignedTo.toLowerCase().includes(query)) ||
      (code.data.assignedEmail && code.data.assignedEmail.toLowerCase().includes(query))
    );
  };

  const formatDateMMDDYY = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${month}${day}${year}`;
    } catch (e) {
      return 'N/A';
    }
  };

  const isExpired = (expiresDate) => {
    if (!expiresDate) return false;
    try {
      const expires = new Date(expiresDate);
      const now = new Date();
      return expires < now;
    } catch (e) {
      return false;
    }
  };

  const getUsesCount = (codeData) => {
    // Get actual used_count from database, or fallback to 0
    return codeData.data.usedCount !== undefined ? codeData.data.usedCount : 0;
  };

  const handleBulkDelete = async () => {
    if (selectedCodes.size === 0) {
      alert('⚠️ Please select at least one code to delete.');
      return;
    }

    const count = selectedCodes.size;
    if (!window.confirm(`Are you sure you want to delete ${count} access code${count > 1 ? 's' : ''}?\n\nThis action cannot be undone.`)) {
      return;
    }

    const allCodes = [...databaseCodes, ...generatedCodes].filter((code, index, self) => 
      index === self.findIndex(c => c.code === code.code)
    );
    
    const codesToDelete = allCodes.filter(c => selectedCodes.has(c.code));
    let successCount = 0;
    let failCount = 0;

    for (const code of codesToDelete) {
      try {
        // If code is in database, delete from database
        if (code.fromDatabase) {
          const response = await fetch(`/api/invite-codes/${encodeURIComponent(code.code)}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error('Failed to delete from database');
          }
        }

        // Remove from local state
        setGeneratedCodes(prev => prev.filter(c => c.code !== code.code));
        setDatabaseCodes(prev => prev.filter(c => c.code !== code.code));
        successCount++;
      } catch (error) {
        console.error(`Error deleting code ${code.code}:`, error);
        failCount++;
      }
    }

    // Clear selection
    setSelectedCodes(new Set());

    if (failCount === 0) {
      alert(`✅ Successfully deleted ${successCount} access code${successCount > 1 ? 's' : ''}!`);
    } else {
      alert(`⚠️ Deleted ${successCount} code${successCount > 1 ? 's' : ''}, but ${failCount} failed.`);
    }
  };

  const handleDeleteCode = async (code) => {
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete access code "${code.code}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      // If code is in database, delete from database
      if (code.fromDatabase) {
        const response = await fetch(`/api/invite-codes/${encodeURIComponent(code.code)}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete access code from database');
        }
      }

      // Remove from local state (for both database and local-only codes)
      setGeneratedCodes(generatedCodes.filter(c => c.code !== code.code));
      setDatabaseCodes(databaseCodes.filter(c => c.code !== code.code));
      
      alert(`✅ Invite code "${code.code}" deleted successfully`);
    } catch (error) {
      console.error('Error deleting code:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleDeleteUser = async (user) => {
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete user "${user.username}" (${user.email})?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      console.log('Deleting user:', user);
      console.log('User ID:', user.id, 'Type:', typeof user.id);
      
      const response = await fetch(`/api/users/${user.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const responseText = await response.text();
      let errorData;
      
      try {
        errorData = JSON.parse(responseText);
      } catch (e) {
        errorData = { error: responseText || 'Unknown error' };
      }

      if (response.ok) {
        // Remove from local state
        setUsers(users.filter(u => u.id !== user.id));
        alert(`✅ User "${user.username}" deleted successfully`);
      } else {
        console.error('Delete user error response:', response.status, errorData);
        throw new Error(errorData.error || `Failed to delete user (Status: ${response.status})`);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        user: user
      });
      alert(`❌ Error: ${error.message}`);
    }
  };

  const copyAllCodes = () => {
    const allCodes = generatedCodes.map(c => 
      `  '${c.code}': {
    used: false,
    expires: ${c.data.expires ? `'${c.data.expires}'` : 'null'},
    maxUses: ${c.data.maxUses !== null ? c.data.maxUses : 'null'},
    created: '${c.data.created}',
    description: '${c.data.description}'
  },`
    ).join('\n');
    
    const fullCode = `export const INVITE_CODES = {\n${allCodes}\n};`;
    
    navigator.clipboard.writeText(fullCode).then(() => {
      alert('All codes copied to clipboard!');
    });
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_config');
    // Clear password field
    setPassword('');
  };

  const openEmailModal = (codeData) => {
    setEmailModal({
      code: codeData.code,
      codeData: codeData.data,
      email: '',
      recipientName: ''
    });
    setEmailSuccess(false);
    setError(null);
  };

  const closeEmailModal = () => {
    setEmailModal(null);
    setEmailSuccess(false);
    setError(null);
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!emailModal || !emailModal.email) {
      setError('Please enter an email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailModal.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setEmailSending(true);
    setError(null);
    setEmailSuccess(false);

    // Get Gmail API Worker URL from localStorage or use default
    const gmailWorkerUrl = localStorage.getItem('gmail_worker_url') || 
                          process.env.REACT_APP_GMAIL_WORKER_URL || 
                          'https://papita-gmail-worker.portilloa3.workers.dev';
    
    // Try Gmail API Worker first (if configured), then fall back to local backend
    const apiUrl = gmailWorkerUrl || '/api/send-invite-email';

    console.log('Sending email to:', apiUrl);
    console.log('Worker URL:', gmailWorkerUrl);

    try {
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to_email: emailModal.email,
          to_name: emailModal.recipientName || 'User',
          invite_code: emailModal.code,
          code_description: emailModal.codeData.description || 'Access code',
          expires: emailModal.codeData.expires || 'Never',
          max_uses: emailModal.codeData.maxUses || 'Unlimited',
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setEmailSuccess(true);
        setTimeout(() => {
          closeEmailModal();
        }, 2000);
      } else {
        throw new Error(data.error || 'Failed to send email. Check Gmail API Worker or local backend configuration.');
      }
    } catch (err) {
      console.error('Email send error:', err);
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack,
        apiUrl: apiUrl,
        gmailWorkerUrl: gmailWorkerUrl
      });
      
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError') || err.name === 'TypeError' || err.message.includes('Network request failed')) {
        // Check if we're using the Worker URL
        if (gmailWorkerUrl && gmailWorkerUrl.startsWith('http')) {
          setError(`Cannot connect to Gmail API Worker at ${gmailWorkerUrl}. Check your internet connection and try again. If the problem persists, the Worker may be temporarily unavailable.`);
        } else {
          setError('Gmail API Worker URL not configured. Using default: https://papita-gmail-worker.portilloa3.workers.dev. If this error persists, check Cloudflare Pages deployment status.');
        }
      } else {
        setError(err.message || 'Failed to send email. Check Gmail API configuration.');
      }
    } finally {
      setEmailSending(false);
    }
  };

  const openEditModal = (codeData) => {
    // Format expires date for input (YYYY-MM-DD)
    let expiresDate = '';
    if (codeData.data.expires) {
      try {
        const date = new Date(codeData.data.expires);
        expiresDate = date.toISOString().split('T')[0];
      } catch (e) {
        expiresDate = '';
      }
    }

    setEditModal({
      code: codeData.code,
      codeData: codeData.data,
      description: codeData.data.description || '',
      expires: expiresDate,
      maxUses: codeData.data.maxUses !== null ? codeData.data.maxUses : '',
      allowWithoutRegistration: codeData.data.allowWithoutRegistration === true,
      active: codeData.data.active !== false // Default to true if not present
    });
    setError(null);
  };

  const closeEditModal = () => {
    setEditModal(null);
    setError(null);
  };

  const handleSyncCode = async (codeData) => {
    try {
      setError(null);
      const code = codeData.code;
      const data = codeData.data;

      // Prepare code data for sync
      const expiresDate = data.expires ? new Date(data.expires + 'T23:59:59').toISOString() : null;
      const maxUsesValue = data.maxUses !== null ? data.maxUses : null;
      const allowWithoutReg = data.allowWithoutRegistration === true;
      const activeValue = data.active !== false; // Default to true

      const codePayload = {
        code: code,
        expires: expiresDate,
        maxUses: maxUsesValue,
        description: data.description || 'Generated via admin dashboard',
        createdBy: 'admin',
        allowWithoutRegistration: allowWithoutReg,
        active: activeValue
      };

      // Try POST first (create new code)
      let response = await fetch('/api/invite-codes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(codePayload),
      });

      // If code already exists (409 conflict), try PUT (update existing)
      if (response.status === 409) {
        console.log(`Code "${code}" already exists, updating instead...`);
        response = await fetch(`/api/invite-codes/${encodeURIComponent(code)}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            expires: expiresDate,
            maxUses: maxUsesValue,
            description: codePayload.description,
            allowWithoutRegistration: allowWithoutReg,
            active: activeValue
          }),
        });
      }

      if (response.ok) {
        const responseData = await response.json();
        
        // Update local state - remove from generatedCodes, add to databaseCodes
        setGeneratedCodes(prev => prev.filter(c => c.code !== code));
        
        // If PUT, response format is different - check structure
        const savedCode = responseData.code || { code: code };
        setDatabaseCodes(prev => {
          // Remove if exists, then add updated version
          const filtered = prev.filter(c => c.code !== code);
          return [...filtered, {
            code: savedCode.code || code,
            data: {
              ...data,
              expires: data.expires || null,
              maxUses: maxUsesValue,
              allowWithoutRegistration: allowWithoutReg,
              active: activeValue
            },
            fromDatabase: true
          }];
        });

        alert(`✅ Code "${code}" successfully synced to database!`);
        
        // Reload codes to get latest data
        loadDatabaseCodes();
      } else {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(errorData.error || 'Failed to sync code to database');
      }
    } catch (error) {
      console.error('Error syncing code:', error);
      setError(error.message || 'Failed to sync code to database. Check database connection.');
      alert(`❌ Error: ${error.message || 'Failed to sync code to database. Check database connection.'}`);
    }
  };

  const handleUpdateCode = async (e) => {
    e.preventDefault();
    if (!editModal) {
      return;
    }

    setUpdatingCode(true);
    setError(null);

    try {
      // Prepare updates object
      const updates = {};
      
      if (editModal.description !== undefined) {
        updates.description = editModal.description.trim() || null;
      }
      
      // Handle expires date
      if (editModal.expires && editModal.expires.trim()) {
        // Convert to ISO string with end of day
        const expiresDate = new Date(editModal.expires + 'T23:59:59');
        updates.expires = expiresDate.toISOString();
      } else {
        updates.expires = null;
      }
      
      // Handle maxUses
      if (editModal.maxUses && editModal.maxUses.toString().trim()) {
        const maxUses = parseInt(editModal.maxUses);
        if (!isNaN(maxUses) && maxUses > 0) {
          updates.maxUses = maxUses;
        } else {
          updates.maxUses = null;
        }
      } else {
        updates.maxUses = null;
      }
      
      // Handle allowWithoutRegistration
      updates.allowWithoutRegistration = editModal.allowWithoutRegistration === true;
      
      // Handle active
      updates.active = editModal.active === true;

      // Call API to update code
      const response = await fetch(`/api/invite-codes/${encodeURIComponent(editModal.code)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update access code');
      }

      const data = await response.json();

      // Update local state - find the code in databaseCodes or generatedCodes
      const allCodes = [...databaseCodes, ...generatedCodes];
      const existingCode = allCodes.find(c => c.code === editModal.code);
      
      const updatedCodeData = {
        code: data.code.code,
        data: {
          ...(existingCode ? existingCode.data : {}),
          ...updates,
          expires: updates.expires || null,
          maxUses: updates.maxUses || null,
          allowWithoutRegistration: updates.allowWithoutRegistration,
          active: updates.active
        },
        fromDatabase: true
      };

      // Update in databaseCodes
      setDatabaseCodes(prev => 
        prev.map(c => c.code === editModal.code ? updatedCodeData : c)
      );

      // Update in generatedCodes if exists
      setGeneratedCodes(prev => 
        prev.map(c => c.code === editModal.code ? updatedCodeData : c)
      );

      alert(`✅ Access code "${editModal.code}" updated successfully!`);
      closeEditModal();
      
      // Reload codes to get latest data
      loadDatabaseCodes();
    } catch (error) {
      console.error('Error updating code:', error);
      setError(error.message || 'Failed to update access code');
    } finally {
      setUpdatingCode(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login-blank">
        <form onSubmit={handleLogin} className="admin-login-form-blank">
          <input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleLogin(e);
              }
            }}
            placeholder=""
            className="admin-password-input-blank"
            autoFocus
            autoComplete="off"
          />
          {error && <div className="admin-error-blank">{error}</div>}
        </form>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>🔐 Admin Dashboard</h1>
      </div>

      <div className="admin-layout">
        {/* Sidebar Navigation */}
        <aside className="admin-sidebar">
          <nav className="admin-nav">
            <button 
              className={`admin-nav-item ${activeSection === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveSection('dashboard')}
            >
              <span className="nav-icon">📊</span>
              <span className="nav-label">Dashboard</span>
            </button>
            <button 
              className={`admin-nav-item ${activeSection === 'generate-code' ? 'active' : ''}`}
              onClick={() => setActiveSection('generate-code')}
            >
              <span className="nav-icon">➕</span>
              <span className="nav-label">Generate Code</span>
            </button>
            <button 
              className={`admin-nav-item ${activeSection === 'invite-codes' ? 'active' : ''}`}
              onClick={() => setActiveSection('invite-codes')}
            >
              <span className="nav-icon">🎫</span>
              <span className="nav-label">Access Codes</span>
            </button>
            <button 
              className={`admin-nav-item ${activeSection === 'users' ? 'active' : ''}`}
              onClick={() => setActiveSection('users')}
            >
              <span className="nav-icon">👥</span>
              <span className="nav-label">Users</span>
            </button>
            <button 
              className={`admin-nav-item ${activeSection === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveSection('settings')}
            >
              <span className="nav-icon">⚙️</span>
              <span className="nav-label">Settings</span>
            </button>
          </nav>
          <div style={{ marginTop: 'auto', padding: '15px', borderTop: '1px solid #e0e0e0' }}>
            <button 
              onClick={handleLogout} 
              className="admin-logout-btn"
              style={{
                width: '100%',
                padding: '12px',
                background: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <span>🚪</span>
              <span>Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <div className="admin-content">
        {/* Dashboard Section */}
        {activeSection === 'dashboard' && (
          <section className="admin-section">
            <h2>📊 Dashboard Overview</h2>
            <div className="dashboard-stats">
              <div className="stat-card">
                <div className="stat-value">{generatedCodes.length}</div>
                <div className="stat-label">Total Codes Generated</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{generatedCodes.filter(c => !c.data.used).length}</div>
                <div className="stat-label">Active Codes</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{generatedCodes.filter(c => c.data.used).length}</div>
                <div className="stat-label">Used Codes</div>
              </div>
            </div>
          </section>
        )}

        {/* Generate Code Section */}
        {activeSection === 'generate-code' && (
          <section className="admin-section">
          <h2>Generate Access Code</h2>
          
          <div className="admin-form">
            <div className="form-group">
              <label>Code Prefix: <span style={{color: 'red'}}>*</span></label>
              <input
                type="text"
                value={newCodeConfig.prefix}
                onChange={(e) => setNewCodeConfig({...newCodeConfig, prefix: e.target.value.toUpperCase()})}
                placeholder="PAPITA"
                maxLength="10"
                required
              />
            </div>

            <div className="form-group">
              <label>Description:</label>
              <input
                type="text"
                value={newCodeConfig.description}
                onChange={(e) => setNewCodeConfig({...newCodeConfig, description: e.target.value})}
                placeholder="e.g., Friend's access code"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Expires (YYYY-MM-DD): <span style={{color: 'red'}}>*</span></label>
                <input
                  type="date"
                  value={newCodeConfig.expires}
                  onChange={(e) => setNewCodeConfig({...newCodeConfig, expires: e.target.value})}
                  disabled={newCodeConfig.neverExpire}
                  required={!newCodeConfig.neverExpire}
                  style={{ opacity: newCodeConfig.neverExpire ? 0.5 : 1 }}
                />
              </div>

              <div className="form-group">
                <label>Max Uses:</label>
                <input
                  type="number"
                  value={newCodeConfig.maxUses}
                  onChange={(e) => setNewCodeConfig({...newCodeConfig, maxUses: parseInt(e.target.value) || 10})}
                  min="1"
                  defaultValue="10"
                  disabled={newCodeConfig.neverExpire}
                  style={{ opacity: newCodeConfig.neverExpire ? 0.5 : 1 }}
                />
              </div>

              <div className="form-group">
                <label>Count: <span style={{color: 'red'}}>*</span></label>
                <input
                  type="number"
                  value={newCodeConfig.count}
                  onChange={(e) => setNewCodeConfig({...newCodeConfig, count: parseInt(e.target.value) || 1})}
                  min="1"
                  max="50"
                  required={!newCodeConfig.neverExpire}
                  disabled={newCodeConfig.neverExpire}
                  style={{ opacity: newCodeConfig.neverExpire ? 0.5 : 1 }}
                />
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newCodeConfig.allowWithoutRegistration}
                  onChange={(e) => setNewCodeConfig({...newCodeConfig, allowWithoutRegistration: e.target.checked})}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '1rem', fontWeight: '500' }}>
                  Allow use without registration
                </span>
              </label>
              <p style={{ marginTop: '8px', fontSize: '0.9rem', color: '#666', marginLeft: '30px' }}>
                {newCodeConfig.allowWithoutRegistration 
                  ? '✓ Users can use this access code without registering first'
                  : '⚠ Users must register before using this access code'}
              </p>
            </div>

            <div className="form-group" style={{ marginTop: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newCodeConfig.neverExpire}
                  onChange={(e) => {
                    const neverExpire = e.target.checked;
                    setNewCodeConfig({
                      ...newCodeConfig, 
                      neverExpire: neverExpire,
                      expires: neverExpire ? '' : newCodeConfig.expires, // Clear expires if neverExpire is checked
                      maxUses: neverExpire ? null : newCodeConfig.maxUses, // Clear maxUses if neverExpire is checked
                      count: neverExpire ? 1 : newCodeConfig.count // Set count to 1 if neverExpire is checked
                    });
                  }}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '1rem', fontWeight: '500' }}>
                  Never Expire (Employers Only)
                </span>
              </label>
              <p style={{ marginTop: '8px', fontSize: '0.9rem', color: '#666', marginLeft: '30px' }}>
                {newCodeConfig.neverExpire 
                  ? '✓ This access code will never expire (starts from today)'
                  : '⚠ Access code will expire based on the date selected above'}
              </p>
            </div>

            {error && (
              <div style={{
                background: '#fee',
                color: '#c33',
                padding: '12px',
                borderRadius: '6px',
                marginTop: '15px',
                fontSize: '0.9rem',
                border: '1px solid #fcc'
              }}>
                ⚠️ {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <button 
                onClick={generateQuickCode} 
                className="admin-generate-btn"
                disabled={savingCodes || (!newCodeConfig.neverExpire && !newCodeConfig.expires)}
                style={{
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  cursor: (savingCodes || (!newCodeConfig.neverExpire && !newCodeConfig.expires)) ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  flex: '0 0 auto',
                  opacity: (!newCodeConfig.neverExpire && !newCodeConfig.expires) ? 0.6 : 1
                }}
              >
                ⚡ Generate Quick Code
              </button>
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Validate before generating
                  if (!newCodeConfig.prefix || newCodeConfig.prefix.trim() === '') {
                    setError('Code Prefix is required');
                    alert('⚠️ Code Prefix is required. Please enter a prefix for the access code.');
                    return;
                  }
                  // Only require expires date if neverExpire is false
                  if (!newCodeConfig.neverExpire && (!newCodeConfig.expires || newCodeConfig.expires.trim() === '')) {
                    setError('Expires date is required');
                    alert('⚠️ Expires date is required. Please select an expiration date, or check "Never Expire".');
                    return;
                  }
                  // Count validation - when neverExpire is checked, count is always 1, so skip validation
                  if (!newCodeConfig.neverExpire && (!newCodeConfig.count || parseInt(newCodeConfig.count) < 1)) {
                    setError('Count is required and must be at least 1');
                    alert('⚠️ Count is required. Please enter the number of codes to generate (minimum 1).');
                    return;
                  }
                  generateInviteCode(e);
                }}
                className="admin-generate-btn"
                disabled={savingCodes || (!newCodeConfig.neverExpire && !newCodeConfig.expires) || !newCodeConfig.prefix}
                style={{ flex: '1' }}
              >
                {savingCodes ? 'Saving to Database...' : `Generate Code${newCodeConfig.count > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>

          {/* Display newly generated code */}
          {lastGeneratedCode && (
            <div style={{
              marginTop: '30px',
              padding: '20px',
              background: '#e8f5e9',
              border: '2px solid #4caf50',
              borderRadius: '8px'
            }}>
              <h3 style={{ marginTop: 0, color: '#2e7d32' }}>✅ Code Generated Successfully!</h3>
              <div style={{
                background: 'white',
                padding: '15px',
                borderRadius: '6px',
                marginTop: '15px',
                fontFamily: 'monospace',
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#1976d2',
                textAlign: 'center',
                border: '1px solid #4caf50'
              }}>
                {lastGeneratedCode.code}
              </div>
              <div style={{ marginTop: '15px', display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(lastGeneratedCode.code);
                    alert('Code copied to clipboard!');
                  }}
                  style={{
                    padding: '8px 16px',
                    background: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  📋 Copy Code
                </button>
                <button
                  onClick={() => openEmailModal(lastGeneratedCode)}
                  style={{
                    padding: '8px 16px',
                    background: '#ff9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  📧 Email Code
                </button>
                <button
                  onClick={() => handleUseCodeForRegistration(lastGeneratedCode)}
                  style={{
                    padding: '8px 16px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  🎫 Use This Code
                </button>
                <button
                  onClick={() => setLastGeneratedCode(null)}
                  style={{
                    padding: '8px 16px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  ✕ Clear
                </button>
              </div>
            </div>
          )}
        </section>
        )}

        {/* Invite Codes Section - List and Manage */}
        {activeSection === 'invite-codes' && (
          <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}>
          <section className="admin-section" style={{ 
            flex: '1', 
            overflow: 'hidden', 
            display: 'flex', 
            flexDirection: 'column',
            marginBottom: 0
          }}>
            <div className="admin-section-header">
              <h2>Access Codes ({[...databaseCodes, ...generatedCodes].filter((code, index, self) => index === self.findIndex(c => c.code === code.code)).length})</h2>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {selectedCodes.size > 0 && (
                  <button 
                    onClick={handleBulkDelete} 
                    className="admin-copy-all-btn"
                    style={{
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}
                  >
                    🗑️ Delete Selected ({selectedCodes.size})
                  </button>
                )}
                <button onClick={copyAllCodes} className="admin-copy-all-btn">
                  Copy All to Clipboard
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div style={{ 
              padding: '15px', 
              borderBottom: '1px solid #e0e0e0',
              background: '#f8f9fa'
            }}>
              <input
                type="text"
                placeholder="🔍 Search codes by code, person, email, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 15px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  outline: 'none'
                }}
              />
            </div>

            {/* Select All Checkbox */}
            <div style={{ 
              padding: '10px 15px', 
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: '#f8f9fa'
            }}>
              <input
                type="checkbox"
                checked={(() => {
                  const allCodes = getAllCodes();
                  const filtered = filterCodesBySearch(allCodes);
                  return filtered.length > 0 && filtered.every(c => selectedCodes.has(c.code));
                })()}
                onChange={toggleSelectAll}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <label style={{ cursor: 'pointer', fontWeight: '600', userSelect: 'none' }}>
                Select All
              </label>
              {selectedCodes.size > 0 && (
                <span style={{ color: '#666', fontSize: '0.9rem' }}>
                  ({selectedCodes.size} selected)
                </span>
              )}
            </div>

            <div className="codes-list" style={{
              flex: '1',
              overflowY: 'auto',
              overflowX: 'hidden',
              maxHeight: '100%'
            }}>
              {filterCodesBySearch([...databaseCodes, ...generatedCodes]
                .filter((code, index, self) => 
                  index === self.findIndex(c => c.code === code.code)
                ))
                .map((codeData, index) => {
                  const expired = isExpired(codeData.data.expires);
                  const isActive = codeData.data.active !== false; // Check active flag (default to true if not present)
                  const usesCount = getUsesCount(codeData);
                  const maxUses = codeData.data.maxUses !== null ? codeData.data.maxUses : 'Unlimited';
                  const expiresFormatted = formatDateMMDDYY(codeData.data.expires);
                  
                  // Find user assigned to this code by looking up users with matching invite_code
                  let assignedTo = 'Not assigned';
                  let assignedEmail = 'Not assigned';
                  const assignedUser = users.find(user => user.inviteCode && user.inviteCode.toUpperCase() === codeData.code.toUpperCase());
                  if (assignedUser) {
                    // User found - use their name and email
                    if (assignedUser.firstName || assignedUser.lastName) {
                      assignedTo = `${assignedUser.firstName || ''} ${assignedUser.lastName || ''}`.trim();
                    } else {
                      assignedTo = assignedUser.username || 'Not assigned';
                    }
                    assignedEmail = assignedUser.email || 'Not assigned';
                  } else {
                    // No user found - try to parse from description as fallback
                    if (codeData.data.description) {
                      const desc = codeData.data.description;
                      const personMatch = desc.match(/Person:\s*([^,]+)/i);
                      const emailMatch = desc.match(/Email:\s*([^\s,]+)/i);
                      if (personMatch) assignedTo = personMatch[1].trim();
                      if (emailMatch) assignedEmail = emailMatch[1].trim();
                    }
                  }
                  
                  return (
                <div key={index} className="code-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '15px', padding: '15px' }}>
                  <input
                    type="checkbox"
                    checked={selectedCodes.has(codeData.code)}
                    onChange={() => toggleCodeSelection(codeData.code)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                  />
                  <div style={{ flex: '1' }}>
                  <div className="code-header">
                    <div className="code-display">
                      <span className="code-value">{codeData.code}</span>
                      {codeData.fromDatabase && (
                        <span className="code-badge" style={{
                          background: '#28a745',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          marginLeft: '10px'
                        }}>✓ Saved</span>
                      )}
                      {codeData.saveError && (
                        <>
                          <span className="code-badge" style={{
                            background: '#dc3545',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            marginLeft: '10px'
                          }}>⚠ Not Saved</span>
                          <button
                            onClick={() => handleSyncCode(codeData)}
                            className="code-sync-btn"
                            title="Sync this code to database"
                            style={{
                              background: '#17a2b8',
                              color: 'white',
                              border: 'none',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              fontWeight: '600',
                              marginLeft: '8px'
                            }}
                          >
                            🔄 Sync
                          </button>
                        </>
                      )}
                      {!codeData.fromDatabase && !codeData.saveError && (
                        <button
                          onClick={() => handleSyncCode(codeData)}
                          className="code-sync-btn"
                          title="Sync this code to database"
                          style={{
                            background: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            fontWeight: '600',
                            marginLeft: '10px'
                          }}
                        >
                          🔄 Sync
                        </button>
                      )}
                    </div>
                    <div className="code-actions">
                      <button 
                        onClick={() => handleUseCodeForRegistration(codeData)}
                        className="code-use-btn"
                        title="Use this code for registration"
                        style={{
                          background: '#667eea',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          marginRight: '8px'
                        }}
                      >
                        🎫 Use This Code
                      </button>
                      <button 
                        onClick={() => copyCodeString(codeData)}
                        className="code-copy-string-btn"
                        title="Copy just the code"
                        style={{
                          background: '#6c757d',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          marginRight: '8px'
                        }}
                      >
                        📋 Copy Code
                      </button>
                      <button 
                        onClick={() => openEditModal(codeData)}
                        className="code-edit-btn"
                        title="Edit this code"
                        style={{
                          background: '#ff9800',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          marginRight: '8px'
                        }}
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        onClick={() => openEmailModal(codeData)}
                        className="code-email-btn"
                        title="Email this code"
                        style={{ marginRight: '8px' }}
                      >
                        📧 Email
                      </button>
                      <button 
                        onClick={() => copyCodeToClipboard(codeData)}
                        className="code-copy-btn"
                        title="Copy code snippet"
                        style={{ marginRight: '8px' }}
                      >
                        Copy Snippet
                      </button>
                      <button 
                        onClick={() => handleDeleteCode(codeData)}
                        className="code-delete-btn"
                        title="Delete this code"
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                  <div className="code-details" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '15px',
                    marginTop: '15px',
                    padding: '15px',
                    background: '#f8f9fa',
                    borderRadius: '6px',
                    border: expired ? '2px solid #dc3545' : '1px solid #e0e0e0'
                  }}>
                    <div>
                      <strong style={{color: '#666', fontSize: '0.85rem'}}>Person Assigned:</strong>
                      <div style={{marginTop: '5px', fontSize: '0.95rem'}}>{assignedTo}</div>
                    </div>
                    <div>
                      <strong style={{color: '#666', fontSize: '0.85rem'}}>Email Assigned:</strong>
                      <div style={{marginTop: '5px', fontSize: '0.95rem'}}>{assignedEmail}</div>
                    </div>
                    <div>
                      <strong style={{color: '#666', fontSize: '0.85rem'}}>Expires Date:</strong>
                      <div style={{marginTop: '5px', fontSize: '0.95rem', fontFamily: 'monospace'}}>{expiresFormatted}</div>
                    </div>
                    <div>
                      <strong style={{color: '#666', fontSize: '0.85rem'}}>Max Uses:</strong>
                      <div style={{marginTop: '5px', fontSize: '0.95rem'}}>{maxUses}</div>
                    </div>
                    <div>
                      <strong style={{color: '#666', fontSize: '0.85rem'}}>Number of Uses:</strong>
                      <div style={{marginTop: '5px', fontSize: '0.95rem', fontWeight: '600'}}>{usesCount}</div>
                    </div>
                    <div>
                      <strong style={{color: '#666', fontSize: '0.85rem'}}>Expired Status:</strong>
                      <div style={{
                        marginTop: '5px',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: expired ? '#dc3545' : '#28a745'
                      }}>
                        {expired ? '❌ Expired' : '✅ Not Expired'}
                      </div>
                    </div>
                    <div>
                      <strong style={{color: '#666', fontSize: '0.85rem'}}>Activated:</strong>
                      <div style={{
                        marginTop: '5px',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: isActive ? '#28a745' : '#dc3545'
                      }}>
                        {isActive ? '✅ Active' : '❌ Inactive (requires registration)'}
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
                  );
                })}
            </div>
          </section>
          </div>
        )}

        {/* Users Section */}
        {activeSection === 'users' && (
          <section className="admin-section">
            <div className="admin-section-header">
              <h2>👥 User Management ({users.length})</h2>
            </div>

            {usersLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>Loading users...</p>
              </div>
            ) : users.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>No users found.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {users.map((user) => (
                  <div key={user.id} style={{
                    background: '#f8f9fa',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ flex: '1' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                        <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{user.username}</h3>
                        {user.isActive ? (
                          <span style={{
                            background: '#28a745',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '0.75rem'
                          }}>Active</span>
                        ) : (
                          <span style={{
                            background: '#dc3545',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '0.75rem'
                          }}>Inactive</span>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', fontSize: '0.9rem', color: '#666' }}>
                        <div>
                          <strong>Email:</strong> {user.email}
                        </div>
                        {user.firstName && (
                          <div>
                            <strong>Name:</strong> {user.firstName} {user.lastName || ''}
                          </div>
                        )}
                        <div>
                          <strong>Access Code:</strong> {user.inviteCode || 'N/A'}
                        </div>
                        <div>
                          <strong>Created:</strong> {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                        </div>
                        {user.lastLogin && (
                          <div>
                            <strong>Last Login:</strong> {new Date(user.lastLogin).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ marginLeft: '20px' }}>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Settings Section */}
        {activeSection === 'settings' && (
          <section className="admin-section">
            <h2>⚙️ Settings</h2>
            <p>Admin settings and configuration coming soon...</p>
            <p>This section will allow you to:</p>
            <ul className="admin-instructions">
              <li>Update admin password</li>
              <li>Configure email settings</li>
              <li>Manage system preferences</li>
              <li>View system logs</li>
            </ul>
          </section>
        )}
      </div>
      </div>

      {/* Email Modal */}
      {emailModal && (
        <div className="email-modal-overlay" onClick={closeEmailModal}>
          <div className="email-modal" onClick={(e) => e.stopPropagation()}>
            <div className="email-modal-header">
              <h3>📧 Send Access Code</h3>
              <button onClick={closeEmailModal} className="email-modal-close">×</button>
            </div>
            
            <div className="email-modal-content">
              <div className="email-code-preview">
                <strong>Code:</strong> <span className="email-code-value">{emailModal.code}</span>
              </div>

              <form onSubmit={sendEmail} className="email-form">
                <div className="form-group">
                  <label>Recipient Email *</label>
                  <input
                    type="email"
                    value={emailModal.email}
                    onChange={(e) => setEmailModal({...emailModal, email: e.target.value})}
                    placeholder="user@example.com"
                    required
                    disabled={emailSending}
                  />
                </div>

                <div className="form-group">
                  <label>Recipient Name (Optional)</label>
                  <input
                    type="text"
                    value={emailModal.recipientName}
                    onChange={(e) => setEmailModal({...emailModal, recipientName: e.target.value})}
                    placeholder="John Doe"
                    disabled={emailSending}
                  />
                </div>

                {error && <div className="email-error">{error}</div>}
                {emailSuccess && <div className="email-success">✓ Email sent successfully!</div>}

                <div className="email-modal-actions">
                  <button 
                    type="button" 
                    onClick={closeEmailModal}
                    className="email-cancel-btn"
                    disabled={emailSending}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="email-send-btn"
                    disabled={emailSending || !emailModal.email}
                  >
                    {emailSending ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </form>

              <div className="email-config-note">
                <p><strong>Gmail API Configuration:</strong></p>
                <div style={{background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '6px', padding: '15px', marginBottom: '15px'}}>
                  <p style={{margin: '0 0 10px 0', color: '#155724', fontWeight: 'bold'}}>
                    ✅ Gmail API Worker is configured and ready!
                  </p>
                  <p style={{margin: '0', color: '#155724', fontSize: '0.9rem'}}>
                    Using: <strong>https://papita-gmail-worker.portilloa3.workers.dev</strong>
                  </p>
                  <p style={{margin: '10px 0 0 0', color: '#155724', fontSize: '0.85rem'}}>
                    Emails will be sent from <strong>portilloa3@gmail.com</strong> via Gmail API.
                  </p>
                </div>
                
                <p style={{marginTop: '15px', fontSize: '0.9rem', color: '#666'}}>
                  <strong>Note:</strong> The Gmail access token expires in ~1 hour. For production use, 
                  set up refresh tokens for automatic renewal. See <code>GMAIL_API_SETUP.md</code> for details.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="email-modal-overlay" onClick={closeEditModal}>
          <div className="email-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="email-modal-header">
              <h3>✏️ Edit Access Code</h3>
              <button onClick={closeEditModal} className="email-modal-close">×</button>
            </div>
            
            <div className="email-modal-content">
              <div className="email-code-preview">
                <strong>Code:</strong> <span className="email-code-value">{editModal.code}</span>
              </div>

              <form onSubmit={handleUpdateCode} className="email-form">
                <div className="form-group">
                  <label>Description:</label>
                  <input
                    type="text"
                    value={editModal.description}
                    onChange={(e) => setEditModal({...editModal, description: e.target.value})}
                    placeholder="e.g., Friend's access code"
                    disabled={updatingCode}
                  />
                </div>

                <div className="form-group">
                  <label>Expires (YYYY-MM-DD):</label>
                  <input
                    type="date"
                    value={editModal.expires}
                    onChange={(e) => setEditModal({...editModal, expires: e.target.value})}
                    disabled={updatingCode}
                  />
                  <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '5px', display: 'block' }}>
                    Leave empty for no expiration
                  </small>
                </div>

                <div className="form-group">
                  <label>Max Uses:</label>
                  <input
                    type="number"
                    value={editModal.maxUses}
                    onChange={(e) => setEditModal({...editModal, maxUses: e.target.value})}
                    min="1"
                    placeholder="Unlimited"
                    disabled={updatingCode}
                  />
                  <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '5px', display: 'block' }}>
                    Leave empty for unlimited uses
                  </small>
                </div>

                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editModal.allowWithoutRegistration}
                      onChange={(e) => setEditModal({...editModal, allowWithoutRegistration: e.target.checked})}
                      disabled={updatingCode}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                      Allow use without registration (Quick Access)
                    </span>
                  </label>
                  <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '5px', display: 'block', marginLeft: '28px' }}>
                    If checked, users can access with this code without registering first
                  </small>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editModal.active}
                      onChange={(e) => setEditModal({...editModal, active: e.target.checked})}
                      disabled={updatingCode}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                      Active
                    </span>
                  </label>
                  <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '5px', display: 'block', marginLeft: '28px' }}>
                    If unchecked, code requires registration before use
                  </small>
                </div>

                {error && <div className="email-error">{error}</div>}

                <div className="email-modal-actions">
                  <button 
                    type="button" 
                    onClick={closeEditModal}
                    className="email-cancel-btn"
                    disabled={updatingCode}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="email-send-btn"
                    disabled={updatingCode}
                  >
                    {updatingCode ? 'Updating...' : 'Update Code'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
